import { ArrowRight, Megaphone, Pin } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { categoryMeta, relativeTime } from '@/features/announcements/meta'
import type { FeedItem } from '@/features/announcements/types'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'

export function LatestNotices({ items, href }: { items: FeedItem[]; href: string }) {
  const { t } = useTranslation('dashboard')
  const [now] = useState(() => Date.now())
  return (
    <Card>
      <CardHeader
        title={t('Latest notices')}
        icon={<Megaphone />}
        actions={
          <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
            <Link to={href}>{t('All notices')}</Link>
          </Button>
        }
      />
      {items.length === 0 ? (
        <EmptyState title={t('No published notices')} compact />
      ) : (
        <ul className="divide-y divide-line">
          {items.map((n) => {
            const meta = categoryMeta[n.category]
            const Icon = meta.icon
            return (
              <li key={n.id} className="flex items-start gap-3 px-5 py-3.5">
                <span className={cn('grid size-8 shrink-0 place-items-center rounded-control', meta.tile)}>
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 truncate text-sm font-medium text-ink">
                    {n.isPinned ? (
                      <Pin className="size-3.5 shrink-0 text-brand-600" aria-label={t('Pinned')} />
                    ) : null}
                    <span className="truncate">{n.title}</span>
                  </p>
                  <p className="truncate text-xs text-muted">
                    {tAny(meta.label)} · {relativeTime(n.publishedAt, now)}
                  </p>
                </div>
                {!n.isRead ? (
                  <span
                    className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-500"
                    aria-label={t('Unread')}
                  />
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
